import { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { type SxProps, type Theme } from "@mui/material/styles";

import { Box } from "ui/box";
import { Button } from "ui/button";
import { DialogActions, DialogBackButton } from "ui/dialog";
import { Select } from "ui/select";
import { TextField } from "ui/text-field";

import {
  mapDictionaryItemsToMultiSelectOptions,
  useGetCampaignObjectiveTypesQuery,
} from "hooks/api/dictionary";

import { useCreateParentTouchpointModal } from "components/modals/create-parent-touchpoint-modal/hooks/use-create-parent-touchpoint-modal";

const FIELDS_SX: SxProps<Theme> = { display: "flex", flexDirection: "column", gap: 2 };

const ParentNewCampaign = () => {
  const { control, getValues, setValue } = useFormContext();

  const { programTypes, audienceDictionaryItems } = useCreateParentTouchpointModal();

  const [newCampaignProgramType, setNewCampaignProgramType] = useState<string>(
    getValues("newCampaignProgramType"),
  );

  const { data: newCampaignObjectiveTypes } =
    useGetCampaignObjectiveTypesQuery(newCampaignProgramType);

  const programTypeOptions = mapDictionaryItemsToMultiSelectOptions(programTypes);
  const objectiveOptions = mapDictionaryItemsToMultiSelectOptions(newCampaignObjectiveTypes);

  const handleNewCampaignProgramTypeChange = (
    value: string,
    fieldOnChange: (value: string) => void,
  ) => {
    setNewCampaignProgramType(value);
    setValue("newCampaignObjective", "", { shouldValidate: true });
    fieldOnChange(value);
  };

  return (
    <div className="w-full flex flex-col">
      <p className="text-gray-600 mb-4">Please edit campaign details as needed and proceed.</p>
      <Box sx={FIELDS_SX}>
        <Controller
          name="newCampaignProgramType"
          control={control}
          render={({ field: { value, onChange, ...field } }) => (
            <Select
              {...field}
              label="Program type"
              selectName="new-campaign-program-type"
              ariaLabel="Program type"
              options={programTypeOptions}
              value={programTypeOptions.find((option) => option.value === value) ?? null}
              onChange={(option) => {
                handleNewCampaignProgramTypeChange(String(option?.value ?? ""), onChange);
              }}
              helperText="This can't be edited once a campaign is created."
            />
          )}
        />
        <Controller
          name="newCampaignName"
          control={control}
          render={({ field }) => <TextField label="Campaign name" required {...field} />}
        />
        <Controller
          name="newCampaignCdnSubdirectoryName"
          control={control}
          render={({ field }) => (
            <TextField
              label="Campaign CDN Profile"
              {...field}
              helperText="Enter a profile name with no spaces. This name will be used to organize this campaign's digital assets and landing pages at its destination URL."
            />
          )}
        />
        <Controller
          name="newCampaignObjective"
          control={control}
          render={({ field: { value, onChange, ...field } }) => (
            <Select
              {...field}
              label="Campaign objective"
              selectName="new-campaign-objective"
              ariaLabel="Campaign objective"
              placeholder="Select campaign objective..."
              options={objectiveOptions}
              value={objectiveOptions.find((option) => option.value === value) ?? null}
              onChange={(option) => {
                onChange(String(option?.value ?? ""));
              }}
            />
          )}
        />
        <Controller
          name="newCampaignAudiences"
          control={control}
          render={({ field }) => (
            <Select
              label="Audience"
              selectName="Audience"
              ariaLabel="Audience"
              multiple
              options={mapDictionaryItemsToMultiSelectOptions(audienceDictionaryItems)}
              value={(field.value || []).map((a: string) => ({ value: a, label: a }))}
              onChange={(selected) => field.onChange(selected.map((s) => s.value as string))}
            />
          )}
        />
      </Box>
    </div>
  );
};

export const ParentNewCampaignActions = () => {
  const { createNewParentCampaign, resetNewCampaignFields, handleStepChange } =
    useCreateParentTouchpointModal();

  const handleBack = () => {
    handleStepChange(1);
    resetNewCampaignFields();
  };

  return (
    <DialogActions
      type="steps"
      back={<DialogBackButton onClick={handleBack}>Back to details</DialogBackButton>}
      primary={
        <Button size="large" onClick={createNewParentCampaign}>
          Continue with email
        </Button>
      }
    />
  );
};

export default ParentNewCampaign;
